/**
 * @mhmo91/ican — `createActor` + bound `me.can`.
 *
 * The boundary builds an actor once per request:
 *
 *     const me = createActor<User>({ uid, orgId, role });
 *     if (me?.can('dispatch', Order, order)) { ... }
 *
 * `data` may be `null` — `createActor` returns `null` rather than
 * throwing. Legacy users with missing/incomplete claims get a null
 * actor; UI buttons stay hidden, server callables return
 * `unauthenticated`.
 *
 * `me.can(action, Entity, data)` is the only check. It walks the
 * entity's rules top-down with deny-overrides + default-deny: any
 * matching deny blocks; otherwise any matching allow grants; nothing
 * matches → blocked.
 */
import type { z } from 'zod';
import { evaluate } from './evaluate.ts';
import { expandPermission } from './permissions.ts';
import type { AccessContext, ActionsOf, AnyEntity, Me, Rule, UserBase, UserInput } from './types.ts';

export function createActor<User extends UserBase>(data: UserInput<User> | null): Me<User> | null {
  if (data === null) return null;
  const userData = data as User;
  if (typeof userData.uid !== 'string' || userData.uid.length === 0) return null;
  const can = <E extends AnyEntity>(
    action: ActionsOf<E>[number],
    entity: E,
    docData: z.infer<E['schema']>,
    context?: AccessContext,
  ): boolean => {
    const rules = entity.rules as ReadonlyArray<Rule<UserBase, unknown, string>>;
    let matchedAllow = false;
    for (const rule of rules) {
      if (!rule.actions.includes(action as string)) continue;
      if (!evaluate(rule.cond, docData, userData, context)) continue;
      if (rule.effect === 'deny') return false;
      matchedAllow = true;
    }
    return matchedAllow;
  };
  const permissions = (userData as { permissions?: readonly string[] }).permissions ?? [];
  const hasPermission = (permission: string): boolean =>
    expandPermission(permission).some((p) => permissions.includes(p));
  return { ...userData, can, hasPermission } as Me<User>;
}
